import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import { guard } from '../../core-transition-component';
import type { GuardFunction, TransitionController } from '../../core-transition-component';
import { useFlowProviderContext } from '../components/FlowProvider';
import type { FlowProviderContextType } from '../components/FlowProvider';

/**
 * Creates gsap.core.Timeline that will start as soon the page is mounted && entire-flow is not hijacked && have executed an optional function before this.
 *
 * @example
 * const myRef = useRef<HTMLDivElement>(null);
 *
 * const transitionController = useTransitionController(() => ({
 *   ref: myRef,
 *   refs: {
 *     myRef,
 *   },
 *   setupTransitionInTimeline(timeline, { myRef }) { ... },
 *   setupTransitionInTimeline(timeline, { myRef }) { ... },
 * }));
 *
 *  useRouteEnterTransition(transitionController, {
 *     beforeTransitionIn: [your GuardFunction],
 *  });
 */
export function useRouteEnterTransition(
  transitionController: TransitionController,
  options?: {
    beforeTransitionIn?: GuardFunction;
  },
  onDispose?: () => void,
): void {
  const { flowHijacked } = useFlowProviderContext() as FlowProviderContextType;

  useIsomorphicLayoutEffect(() => {
    (async () => {
      guard(async () => {
        await flowHijacked.current;
        transitionController?.transitionIn();
      }, options?.beforeTransitionIn);
    })();

    return () => {
      if (onDispose) {
        onDispose();
      }
    };
  }, [transitionController]);
}
